import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import { WbSafeNunjucksApi, wbSafeNunjucksApiRef } from '../apis';
import { useEffect, useState, useMemo } from 'react';

export const parseDisplayField = (
  nunjucksApi: WbSafeNunjucksApi,
  entity: Record<string, any>,
  displayField?: string,
) => {
  if (!displayField) return undefined;

  return nunjucksApi.render(displayField, entity);
};

/** Owns template registration, readiness, and release across template changes. */
export const useLoaderNunjucks = (paths?: string[]) => {
  const nunjucksApi = useApi(wbSafeNunjucksApiRef);

  const [loadedHash, setLoadedHash] = useState<string | undefined>(undefined);

  const experimentalNunjucksRendering =
    useApi(configApiRef).getOptionalBoolean(
      'mesh.experimentalNunjucksRendering',
    ) ?? true;

  // Track template content changes so new array instances don't trigger unnecessary reloads.
  const hash = useMemo(() => JSON.stringify(paths ?? []), [paths]);

  const normalizedPaths = useMemo(() => {
    const templates: string[] = JSON.parse(hash);
    return templates.length ? templates : undefined;
  }, [hash]);

  useEffect(() => {
    let cleanupFn: any;
    if (normalizedPaths && experimentalNunjucksRendering) {
      cleanupFn = nunjucksApi.load(normalizedPaths);
    }
    setLoadedHash(hash);
    return () => {
      if (cleanupFn) {
        cleanupFn();
      }
    };
  }, [experimentalNunjucksRendering, normalizedPaths, hash, nunjucksApi]);

  const returnValue = useMemo(() => {
    return { isReady: loadedHash === hash };
  }, [loadedHash, hash]);

  return returnValue;
};
